// src/components/Store/ProductDetails.js
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PublicLayout from '../public/PublicLayout';

// ARCA Color Scheme
const ARCA_COLORS = {
  primary: '#6B8E23',
  secondary: '#2C5234',
  success: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  light: '#f8f9fa'
};

const ProductDetails = ({ user, onLogout }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState([]);
  const [reviews, setReviews] = useState([]);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000');

  useEffect(() => {
    fetchProduct();

    // Load cart from localStorage
    const savedCart = localStorage.getItem('arcaCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/store/products/${productId}`);
      const data = await response.json();

      if (data.success && data.data) {
        setProduct(data.data);
      } else {
        toast.error('Product not found');
        navigate('/store');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
      navigate('/store');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = () => {
    if (!product) return;

    const existingItem = cart.find(item => item.id === product.id);
    let newCart;

    if (existingItem) {
      newCart = cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
      toast.success(`Added ${quantity} more ${product.name} to cart`);
    } else {
      newCart = [...cart, { ...product, quantity }];
      toast.success(`${product.name} added to cart`);
    }

    setCart(newCart);
    localStorage.setItem('arcaCart', JSON.stringify(newCart));
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border" style={{ color: ARCA_COLORS.primary }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading product details...</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!product) {
    return (
      <PublicLayout>
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <h2 className="display-6 fw-bold mb-4" style={{ color: ARCA_COLORS.secondary }}>Product Not Found</h2>
            <p className="text-muted mb-4">The product you're looking for doesn't exist.</p>
            <Link to="/store" className="btn btn-lg" style={{ backgroundColor: ARCA_COLORS.primary, color: 'white' }}>
              <i className="bi bi-arrow-left me-2"></i>Back to Store
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div style={{ backgroundColor: ARCA_COLORS.light, minHeight: '100vh' }} className="py-4">
        <div className="container">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="mb-3">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/store" style={{ color: ARCA_COLORS.primary }}>
                  <i className="bi bi-shop me-1"></i>Store
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">{product.name}</li>
            </ol>
          </nav>

          {/* Product Details Card */}
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <div className="row g-4">
                {/* Product Image */}
                <div className="col-md-5">
                  <div className="card border-0">
                    {product.main_image ? (
                      <img
                        src={product.main_image.startsWith('http') ? product.main_image : `${BACKEND_URL}${product.main_image}`}
                        alt={product.name}
                        className="card-img-top rounded"
                        style={{ objectFit: 'contain', maxHeight: '400px' }}
                      />
                    ) : (
                      <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                        <i className="bi bi-image text-muted" style={{ fontSize: '4rem' }}></i>
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Info */}
                <div className="col-md-7">
                  {/* Product Name */}
                  <h1 className="h3 fw-bold mb-3" style={{ color: ARCA_COLORS.secondary }}>{product.name}</h1>

                  {/* Price Section */}
                  <div className="mb-4">
                    <div className="d-flex align-items-center gap-3 mb-2">
                      <span className="h2 fw-bold mb-0" style={{ color: ARCA_COLORS.primary }}>
                        {formatCurrency(product.price)}
                      </span>
                      {product.compare_price && product.compare_price > product.price && (
                        <>
                          <span className="h5 text-decoration-line-through text-muted mb-0">
                            {formatCurrency(product.compare_price)}
                          </span>
                          <span className="badge" style={{ backgroundColor: ARCA_COLORS.danger }}>
                            Save {formatCurrency(product.compare_price - product.price)}
                          </span>
                        </>
                      )}
                    </div>
                    {product.member_price && product.member_price < product.price && (
                      <div>
                        <span className="badge" style={{ backgroundColor: ARCA_COLORS.success }}>
                          Member Price: {formatCurrency(product.member_price)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Stock Status */}
                  <div className="mb-4">
                    {product.stock_quantity > 0 ? (
                      <span className="badge" style={{ backgroundColor: ARCA_COLORS.success }}>
                        <i className="bi bi-check-circle me-1"></i>
                        {product.stock_quantity} in stock
                      </span>
                    ) : (
                      <span className="badge bg-secondary">
                        <i className="bi bi-x-circle me-1"></i>
                        Out of stock
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  {product.short_description && (
                    <div className="mb-4">
                      <h5 className="fw-bold mb-2" style={{ color: ARCA_COLORS.secondary }}>Description</h5>
                      <p className="text-muted">{product.short_description}</p>
                    </div>
                  )}

                  {/* Product Details */}
                  <div className="mb-4">
                    <h5 className="fw-bold mb-3" style={{ color: ARCA_COLORS.secondary }}>Product Details</h5>
                    <table className="table table-sm">
                      <tbody>
                        {product.sku && (
                          <tr>
                            <td className="fw-medium" style={{ width: '30%' }}>SKU:</td>
                            <td>{product.sku}</td>
                          </tr>
                        )}
                        {product.brand && (
                          <tr>
                            <td className="fw-medium">Brand:</td>
                            <td>{product.brand}</td>
                          </tr>
                        )}
                        {product.manufacturer && (
                          <tr>
                            <td className="fw-medium">Manufacturer:</td>
                            <td>{product.manufacturer}</td>
                          </tr>
                        )}
                        {product.weight && (
                          <tr>
                            <td className="fw-medium">Weight:</td>
                            <td>{product.weight} kg</td>
                          </tr>
                        )}
                        {product.product_condition && (
                          <tr>
                            <td className="fw-medium">Condition:</td>
                            <td className="text-capitalize">{product.product_condition}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Quantity and Add to Cart */}
                  <div className="mb-3">
                    <div className="row g-2 align-items-center mb-3">
                      <div className="col-auto">
                        <label className="form-label mb-0 fw-medium">Quantity:</label>
                      </div>
                      <div className="col-auto">
                        <input
                          type="number"
                          min="1"
                          max={product.stock_quantity}
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock_quantity, parseInt(e.target.value) || 1)))}
                          className="form-control"
                          style={{ width: '80px' }}
                        />
                      </div>
                    </div>

                    <div className="d-grid gap-2">
                      <button
                        onClick={() => {
                          addToCart();
                          navigate('/checkout');
                        }}
                        className="btn btn-lg fw-bold text-white"
                        style={{ backgroundColor: ARCA_COLORS.secondary, borderColor: ARCA_COLORS.secondary }}
                        disabled={product.stock_quantity === 0}
                      >
                        <i className="bi bi-lightning-fill me-2"></i>
                        Buy Now
                      </button>
                      <button
                        onClick={addToCart}
                        className="btn btn-lg fw-bold text-white"
                        style={{ backgroundColor: ARCA_COLORS.primary, borderColor: ARCA_COLORS.primary }}
                        disabled={product.stock_quantity === 0}
                      >
                        <i className="bi bi-cart-plus me-2"></i>
                        {product.stock_quantity === 0 ? 'Out of Stock' : 'Add to Cart'}
                      </button>
                    </div>
                  </div>

                  {/* Back to Store Link */}
                  <div className="mt-3">
                    <Link to="/store" className="btn btn-outline-secondary">
                      <i className="bi bi-arrow-left me-2"></i>Back to Store
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};
export default ProductDetails;
